import { useEffect } from 'react';
import { create } from 'zustand';
import { useAuthStore } from './auth.store';
import { getBookingPolicies } from '../axios/bookingPolicies';
import { normalizeFlags } from '../flow/flowFlags';

/**
 * Each location's saved booking-flow flags (Settings > Appointment >
 * Appointment Journey), cached so the sidebar can show Categories /
 * Professionals only when they're enabled. Loaded once per location from
 * `GET /location/findById/:id`; whoever saves new flags calls `setFlags` so
 * the menu updates straight away.
 */
export const useBookingFlagsStore = create((set, get) => ({
  /** @type {Object.<string, import('../flow/types').BookingFlowFlags>} */
  flagsByLocation: {},

  /** Fetches a location's flags unless already cached (or `force`). Never throws. */
  async loadFlags(locationId, { force = false } = {}) {
    if (!locationId) return;
    const key = String(locationId);
    if (!force && get().flagsByLocation[key]) return;
    try {
      const response = await getBookingPolicies(locationId);
      const location = response?.data?.data;
      if (!location) return;
      get().setFlags(locationId, {
        serviceOnly: location.serviceOnly ?? true,
        isCategoryEnabled: location.isCategoryEnabled ?? false,
        areProfessionalsEnabled: location.areProfessionalsEnabled ?? false,
        professionalFirst: location.professionalFirst ?? false,
        profAndSerNoAssociation: location.profAndSerNoAssociation ?? false,
        areFreeServicesEnabled: location.areFreeServicesEnabled ?? false
      });
    } catch {
      // Menu items stay hidden until a later load succeeds.
    }
  },

  setFlags(locationId, flags) {
    set((state) => ({
      flagsByLocation: { ...state.flagsByLocation, [String(locationId)]: normalizeFlags(flags) }
    }));
  }
}));

/**
 * One of the signed-in location's booking-flow flags (Appointment Journey).
 * False until its flags load, like the sidebar's Categories item.
 * @param {keyof import('../flow/types').BookingFlowFlags} name
 */
const useLocationFlag = (name) => {
  const locationId = useAuthStore((state) => state.locationId);
  const enabled = useBookingFlagsStore(
    (state) => state.flagsByLocation[String(locationId)]?.[name]
  );
  const loadFlags = useBookingFlagsStore((state) => state.loadFlags);
  useEffect(() => {
    loadFlags(locationId);
  }, [locationId, loadFlags]);
  return Boolean(enabled);
};

export const useCategoryEnabled = () => useLocationFlag('isCategoryEnabled');

export const useFreeServicesEnabled = () => useLocationFlag('areFreeServicesEnabled');
